import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { DEFAULT_POLYGON_COLOR, DEFAULT_POLYGON_FILL_OPACITY } from '../components/PolygonTool/types';
import { elevationService } from '../services/elevationService';
import { polygonSurfaceAreaService } from '../services/polygonSurfaceAreaService';
import { isPointInPolygon, computeCopyOffsetLatLng } from '../utils/geometryUtils';
import { usePickTerrainPosition, useConstraintCheck } from './common/useCesiumInteraction';
import { useCustomDataSource } from './common/useCustomDataSource';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { createBoundingPolygon } from '../utils/polygonUtils';
import { useVisibilityControl } from './useVisibilityControl';
import { useLocalStoragePersistence } from './useLocalStoragePersistence';
import { useUnitPreferences } from './useUnitPreferences';
import { formatAreaFromSqMeters, formatDistance } from '../utils/units';
import { usePolygonDrawing } from './usePolygonDrawing';
import { formatArea, generateVertexId, generatePolygonId, calculatePolygonArea, calculatePerimeter, calculateEdges } from './polygons/polygonUtils';
import { createPolygonVertex, createPolygonOutline, createPolygonFill, createPolygonLabel, createGlowHighlight, makePolylineWidthProperty } from '../utils/cesiumEntityFactory';
import { STORAGE_KEY, serializePolygonsForStorage, deserializeStoredPolygons } from './polygons/polygonSerialization';
// ─── Rotation helpers (local east-north-up frame about a centroid) ──────────
// Rotating in raw lat/lng distorts with latitude, so we project into a local
// metres frame at the centroid, rotate in-plane, and project back.
// Bearing (radians, 0 = north, clockwise) from centroid C to world point P.
const enuBearing = (C, P) => {
    const inv = Cesium.Matrix4.inverse(Cesium.Transforms.eastNorthUpToFixedFrame(C), new Cesium.Matrix4());
    const local = Cesium.Matrix4.multiplyByPoint(inv, P, new Cesium.Cartesian3());
    return Math.atan2(local.x, local.y); // x = east, y = north
};
// Rotate world point P clockwise by `delta` radians about centroid C.
const rotateAroundCentroid = (C, P, delta) => {
    const M = Cesium.Transforms.eastNorthUpToFixedFrame(C);
    const inv = Cesium.Matrix4.inverse(M, new Cesium.Matrix4());
    const local = Cesium.Matrix4.multiplyByPoint(inv, P, new Cesium.Cartesian3());
    const cos = Math.cos(delta);
    const sin = Math.sin(delta);
    const e = local.x * cos + local.y * sin;
    const n = -local.x * sin + local.y * cos;
    return Cesium.Matrix4.multiplyByPoint(M, new Cesium.Cartesian3(e, n, local.z), new Cesium.Cartesian3());
};
// Scale world point P about centroid C by `factor`, in the local ENU plane so
// the shape stays on the surface (height preserved). Used by uniform scale.
const scaleAroundCentroid = (C, P, factor) => {
    const M = Cesium.Transforms.eastNorthUpToFixedFrame(C);
    const inv = Cesium.Matrix4.inverse(M, new Cesium.Matrix4());
    const local = Cesium.Matrix4.multiplyByPoint(inv, P, new Cesium.Cartesian3());
    return Cesium.Matrix4.multiplyByPoint(M, new Cesium.Cartesian3(local.x * factor, local.y * factor, local.z), new Cesium.Cartesian3());
};
// Screen-space gap (in pixels) between the polygon edge and the rotate knob, so
// the stem reads the same length at any zoom or polygon size.
const ROTATE_GAP_PX = 34;
// Metres that ROTATE_GAP_PX pixels span at the polygon's distance from camera.
// Falls back to a small proportional pad if the camera can't report a size.
const rotateGapMeters = (viewer, sphere) => {
    const mpp = viewer.camera.getPixelSize(sphere, viewer.scene.drawingBufferWidth, viewer.scene.drawingBufferHeight);
    return Number.isFinite(mpp) && mpp > 0 ? ROTATE_GAP_PX * mpp : sphere.radius * 0.15 + 6;
};
// World point at `dist` metres from centroid C along `bearing` (0 = north).
const offsetAlongBearing = (C, bearing, dist) => {
    const M = Cesium.Transforms.eastNorthUpToFixedFrame(C);
    return Cesium.Matrix4.multiplyByPoint(M, new Cesium.Cartesian3(Math.sin(bearing) * dist, Math.cos(bearing) * dist, 0), new Cesium.Cartesian3());
};
// Where the ray from centroid C along `bearing` exits the polygon outline, so
// the rotate stem can start at the perimeter instead of slicing the centre.
// Works in the local ENU plane; returns the farthest forward boundary crossing
// (handles convex + concave), or null if none (shouldn't happen for an interior
// centroid).
const boundaryPointAlongBearing = (C, bearing, worldVerts) => {
    const M = Cesium.Transforms.eastNorthUpToFixedFrame(C);
    const inv = Cesium.Matrix4.inverse(M, new Cesium.Matrix4());
    const locals = worldVerts.map(w => Cesium.Matrix4.multiplyByPoint(inv, w, new Cesium.Cartesian3()));
    const dx = Math.sin(bearing);
    const dy = Math.cos(bearing);
    let bestT = -1;
    const n = locals.length;
    for (let i = 0; i < n; i++) {
        const a = locals[i];
        const b = locals[(i + 1) % n];
        const ex = b.x - a.x;
        const ey = b.y - a.y;
        const det = ex * dy - dx * ey;
        if (Math.abs(det) < 1e-9)
            continue;
        const t = (-a.x * ey + ex * a.y) / det; // distance along ray
        const s = (dx * a.y - dy * a.x) / det; // position along edge
        if (t >= 0 && s >= 0 && s <= 1 && t > bestT)
            bestT = t;
    }
    if (bestT < 0)
        return null;
    return Cesium.Matrix4.multiplyByPoint(M, new Cesium.Cartesian3(dx * bestT, dy * bestT, 0), new Cesium.Cartesian3());
};
// Current vertex positions, read live from the entities (so they reflect an
// in-progress drag before state is committed), falling back to stored values.
const livePositionsOf = (polygon) => {
    const now = Cesium.JulianDate.now();
    return polygon.vertices.map(v => { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = v.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, now)) !== null && _d !== void 0 ? _d : v.position; });
};
// Next default name is one past the highest existing "Polygon N", so the sequence
// keeps climbing and never collides or goes backward when earlier polygons are
// deleted. Counting polygons.size regressed on delete.
const nextDefaultPolygonName = (polygons) => {
    const numbers = [...polygons.values()]
        .map(p => { var _a; return (_a = /^Polygon (\d+)$/.exec(p.name)) === null || _a === void 0 ? void 0 : _a[1]; })
        .filter((n) => Boolean(n))
        .map(Number);
    return `Polygon ${(numbers.length ? Math.max(...numbers) : 0) + 1}`;
};
// On-brand transform-chrome palette (handles, rotate knob, readout pills).
const CHROME_FOREST = Cesium.Color.fromCssColorString('#263328');
const CHROME_SAND = Cesium.Color.fromCssColorString('#EEE9E1');
// Azimuth (degrees, 0–360, 0 = north, clockwise) measured ALONG edge A→B.
const edgeAzimuthDeg = (A, B) => {
    const deg = (enuBearing(A, B) * 180) / Math.PI;
    return Math.round(((deg % 360) + 360) % 360);
};
// Compact side-length label (promotes ft→mi, m→km at the usual thresholds).
const formatSideLength = (meters, imperial) => {
    if (imperial) {
        const ft = meters * 3.28084;
        return ft >= 5280 ? `${(ft / 5280).toFixed(2)} mi` : `${Math.round(ft)} ft`;
    }
    return meters >= 1000 ? `${(meters / 1000).toFixed(2)} km` : `${Math.round(meters)} m`;
};
// Cumulative rotate-gesture angle, normalised to (−180°, 180°], for the readout.
const normalizeAngleDeg = (radians) => {
    let deg = ((radians * 180) / Math.PI) % 360;
    if (deg > 180)
        deg -= 360;
    if (deg <= -180)
        deg += 360;
    return Math.round(deg);
};
export const usePolygons = ({ viewer, isActive, projectId, constraintPolygon, editingPolygonId, viewLocked }) => {
    const viewLockedRef = useRef(!!viewLocked);
    useEffect(() => { viewLockedRef.current = !!viewLocked; }, [viewLocked]);
    const [polygons, setPolygons] = useState(new Map());
    // Always-current snapshot of the polygons map for use inside ref-driven event
    // handlers (assigned during render, like the router's bufferRef).
    const polygonsRef = useRef(polygons);
    polygonsRef.current = polygons;
    const [selectedPolygon, setSelectedPolygon] = useState(null);
    // Active-vertex anchor (parity with the path tool's selectedDrawingVertex): a
    // finished polygon may have one "active" vertex shown in yellow. The next
    // click on empty terrain inserts a new vertex right after it in ring order.
    // Tracked by stable vertex id so it survives index shifts from insert/delete.
    const [activeVertex, setActiveVertex] = useState(null);
    const activeVertexRef = useRef(activeVertex);
    activeVertexRef.current = activeVertex;
    const [dragging, setDragging] = useState(null);
    // Undo / redo of GEOMETRY edits (vertex move/insert/delete, whole-polygon
    // move/rotate, create/delete). Each stack entry is a full geometry snapshot
    // (PolygonConfig[]); a snapshot of the pre-change state is pushed at each
    // edit site, and undo/redo rebuild the polygons from a snapshot. Geometry
    // only — async profile/surface-area updates and colour changes don't record.
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const UNDO_LIMIT = 50;
    const captureConfigs = useCallback(() => {
        const out = [];
        polygonsRef.current.forEach((p) => {
            out.push({
                id: p.id,
                name: p.name,
                vertices: p.vertices.map(v => {
                    const c = Cesium.Cartographic.fromCartesian(v.position);
                    return { latitude: Cesium.Math.toDegrees(c.latitude), longitude: Cesium.Math.toDegrees(c.longitude) };
                }),
                color: p.color,
                outlineColor: p.outlineColor,
                unit: p.displayUnit,
                lengthUnit: p.lengthUnit,
                projectId: p.projectId,
                fillOpacity: p.fillOpacity,
                showMeasurements: p.showMeasurements,
            });
        });
        return out;
    }, []);
    const pushUndoSnapshot = useCallback(() => {
        undoStackRef.current.push(captureConfigs());
        if (undoStackRef.current.length > UNDO_LIMIT)
            undoStackRef.current.shift();
        redoStackRef.current = [];
    }, [captureConfigs]);
    const [defaultUnit, setDefaultUnit] = useState(() => {
        try {
            const prefs = JSON.parse(localStorage.getItem('landscope_unit_preferences') || '{}');
            if (['sqm', 'sqft', 'acres', 'hectares'].includes(prefs.areaUnit))
                return prefs.areaUnit;
        }
        catch ( /* ignore */_a) { /* ignore */ }
        return 'acres';
    });
    const [deleteVertexPrompt, setDeleteVertexPrompt] = useState(null);
    const [itemOpacities, setItemOpacities] = useState(new Map());
    const [hoveredEdge, setHoveredEdge] = useState(null);
    const [overlappingPolygons, setOverlappingPolygons] = useState(null);
    const dataSourceRef = useCustomDataSource(viewer, 'polygons');
    const edgeProfileGenRef = useRef(0);
    const holdTimerRef = useRef(null);
    const mouseDownPosRef = useRef(null);
    // True once a whole-polygon translate/rotate gesture has actually changed the
    // shape. Lets a plain grab-without-move (e.g. a select-click on the body, or a
    // knob tap) skip the commit + surface-area/edge refetch that a real move does.
    const translateMovedRef = useRef(false);
    const rotateMovedRef = useRef(false);
    const scaleMovedRef = useRef(false);
    // True while Shift is held — turns a vertex drag into a uniform scale.
    const shiftHeldRef = useRef(false);
    const finishPolygonRef = useRef(() => { });
    const edgeGlowEntityRef = useRef(null);
    // Rotate-handle entities for the selected polygon (a stem line + a grab knob
    // above the centroid). handleBearingRef is where the knob currently sits
    // (radians, 0 = north); it follows the cursor while rotating and persists
    // between gestures so the knob stays where the user left it.
    const rotateStemEntityRef = useRef(null);
    const rotateHandleEntityRef = useRef(null);
    const handleBearingRef = useRef(0);
    // Per-side length+azimuth labels for the selected polygon; the live angle
    // readout pill shown while rotating; and the refs that drive its text/visibility.
    const edgeLabelEntitiesRef = useRef([]);
    const anglePillEntityRef = useRef(null);
    const isRotatingRef = useRef(false);
    const rotationDegRef = useRef(0);
    // Phantom-vertex-on-edge state (matches path tool). While the cursor is
    // over a polygon outline we render a translucent ghost vertex at the
    // cursor's terrain projection to advertise "click to insert a vertex
    // here." Clicking the phantom routes to insertVertexOnEdge.
    const phantomNodeRef = useRef(null);
    const phantomTargetRef = useRef(null);
    const handleDrawingEntityClickRef = useRef(() => false);
    // Freeform (click-per-vertex) vs rectangle (two-corner) drawing. Mirrors the
    // measure tool's line/circle mode. Rectangle bypasses the incremental points
    // buffer and creates the finished polygon directly via the ref below.
    const [polygonDrawMode, setPolygonDrawMode] = useState('freeform');
    const rectangleCompleteRef = useRef(() => { });
    // Opt-in preference for the live draw-time measurements (side lengths + the
    // centroid area). Default OFF — a clean shape while drawing, measurements on
    // demand.
    const [showDrawLabels, setShowDrawLabelsState] = useState(() => localStorage.getItem('landscope_show_draw_labels') === 'true');
    const setShowDrawLabels = useCallback((next) => {
        setShowDrawLabelsState(next);
        try {
            localStorage.setItem('landscope_show_draw_labels', next ? 'true' : 'false');
        }
        catch ( /* ignore */_a) { /* ignore */ }
    }, []);
    // Live (reactive) global unit preference. Draw-time length labels use the
    // distance unit — the same source the HUD's auto-promoting area uses — so
    // lengths and area always agree, and toggling Imperial/Metric moves both.
    const unitPrefs = useUnitPreferences();
    const drawLengthUnit = unitPrefs.distanceUnit;
    // Live area readout painted at the polygon's centroid while drawing, so the
    // number sits on the shape instead of the panel. Renders in the explicit area
    // unit the user picked from the HUD dropdown (no magnitude auto-promotion).
    // Governed by the "Show measurements" toggle — off clears every on-map
    // readout, the area included (returns '' → the label hides).
    const formatCentroidArea = useCallback((areaM2) => (showDrawLabels ? formatAreaFromSqMeters(areaM2, unitPrefs.areaUnit) : ''), [showDrawLabels, unitPrefs.areaUnit]);
    // Fresh-draw mode: tool armed and not editing/selecting an existing polygon.
    const drawingArmed = isActive && !selectedPolygon && !editingPolygonId;
    // Router click consumer (same pattern as paths/measure/placemarks): while
    // armed for fresh drawing, claim every LEFT_CLICK so the existing-feature
    // pick handlers (polygon dialog, server-artifact view) never fire — the
    // private drawing handler then places the vertex uncontested. In edit/select
    // mode this returns false and the router's existing-polygon chain runs.
    const handleMapClick = useCallback((_e) => drawingArmed, [drawingArmed]);
    // Use the shared polygon drawing hook for drawing new polygons
    const drawing = usePolygonDrawing({
        viewer,
        // Suppress new-polygon drawing while editing a server polygon — clicks should not start a fresh polygon.
        isActive: drawingArmed,
        drawMode: polygonDrawMode,
        showLabels: showDrawLabels,
        lengthUnit: drawLengthUnit,
        centroidLabelFormatter: formatCentroidArea,
        onFinishRequested: () => { finishPolygonRef.current(); },
        onRectangleComplete: (corners) => { rectangleCompleteRef.current(corners); },
        onEntityClick: (entity, screenPos) => handleDrawingEntityClickRef.current(entity, screenPos),
        pointColor: Cesium.Color.fromCssColorString(DEFAULT_POLYGON_COLOR),
        pointOutlineColor: Cesium.Color.BLACK,
        polygonFillColor: Cesium.Color.fromCssColorString(DEFAULT_POLYGON_COLOR).withAlpha(0.3),
        polygonOutlineColor: Cesium.Color.fromCssColorString(DEFAULT_POLYGON_COLOR)
    });
    const getPolygonEntities = useCallback((p) => {
        const entities = [];
        // Fill + outline first: useVisibilityControl treats the first defined entity
        // as the item's visibility proxy. Read-only view mode hides the vertex
        // handles (edit affordances), so a vertex-first order made isItemVisible
        // report a viewed saved polygon as "invisible" — which deselected it and
        // flickered its dialog shut on open. The fill/outline reflect true visibility.
        entities.push(p.polygonEntity);
        entities.push(p.outlineEntity);
        p.vertices.forEach(v => entities.push(v.entity));
        entities.push(p.labelEntity);
        return entities;
    }, []);
    const { isVisible, hideAll, showAll, toggleItemVisibility, isItemVisible } = useVisibilityControl(polygons, getPolygonEntities);
    const fetchEdgeProfiles = useCallback((polygonId, edges, positions) => {
        if (!projectId || edges.length === 0)
            return;
        const gen = ++edgeProfileGenRef.current;
        edges.forEach((edge, edgeIdx) => {
            const startPos = positions[edge.startVertexIndex];
            const endPos = positions[edge.endVertexIndex];
            const startCart = Cesium.Cartographic.fromCartesian(startPos);
            const endCart = Cesium.Cartographic.fromCartesian(endPos);
            elevationService.getLineElevationProfile(projectId, Cesium.Math.toDegrees(startCart.latitude), Cesium.Math.toDegrees(startCart.longitude), Cesium.Math.toDegrees(endCart.latitude), Cesium.Math.toDegrees(endCart.longitude), 20).then(profile => {
                if (gen !== edgeProfileGenRef.current)
                    return;
                setPolygons(prev => {
                    const newMap = new Map(prev);
                    const existingPolygon = newMap.get(polygonId);
                    if (existingPolygon && existingPolygon.edges) {
                        const updatedEdges = [...existingPolygon.edges];
                        if (updatedEdges[edgeIdx]) {
                            updatedEdges[edgeIdx] = { ...updatedEdges[edgeIdx], elevationProfile: profile || null };
                        }
                        newMap.set(polygonId, { ...existingPolygon, edges: updatedEdges });
                    }
                    return newMap;
                });
            }).catch(() => {
                if (gen !== edgeProfileGenRef.current)
                    return;
                setPolygons(prev => {
                    const newMap = new Map(prev);
                    const existingPolygon = newMap.get(polygonId);
                    if (existingPolygon && existingPolygon.edges) {
                        const updatedEdges = [...existingPolygon.edges];
                        if (updatedEdges[edgeIdx]) {
                            updatedEdges[edgeIdx] = { ...updatedEdges[edgeIdx], elevationProfile: null };
                        }
                        newMap.set(polygonId, { ...existingPolygon, edges: updatedEdges });
                    }
                    return newMap;
                });
            });
        });
    }, [projectId]);
    // Generation counter so an in-flight surface-area request from a
    // previous geometry version doesn't overwrite a newer one. Mirrors
    // the edgeProfileGenRef pattern above.
    const surfaceAreaGenRef = useRef(new Map());
    // Build a closed-ring GeoJSON polygon string from vertex Cartesians.
    const verticesToGeoJSON = useCallback((vertices) => {
        if (vertices.length < 3)
            return null;
        const coords = vertices.map(v => {
            const c = Cesium.Cartographic.fromCartesian(v.position);
            return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
        });
        coords.push(coords[0]); // close the ring
        return JSON.stringify({ type: 'Polygon', coordinates: [coords] });
    }, []);
    // Fire the lightweight backend surface-area calculation for a polygon
    // and update its record. Safe to call repeatedly; later requests
    // supersede earlier ones via the per-polygon generation counter.
    const fetchSurfaceArea = useCallback((polygonId, vertices, planarAreaM2) => {
        if (!projectId)
            return;
        const geojson = verticesToGeoJSON(vertices);
        if (!geojson)
            return;
        const nextGen = (surfaceAreaGenRef.current.get(polygonId) || 0) + 1;
        surfaceAreaGenRef.current.set(polygonId, nextGen);
        setPolygons(prev => {
            const p = prev.get(polygonId);
            if (!p)
                return prev;
            const next = new Map(prev);
            next.set(polygonId, {
                ...p,
                surfaceAreaLoading: true,
                surfaceAreaSquareMeters: undefined,
                surfaceAreaCoveredFraction: undefined,
            });
            return next;
        });
        polygonSurfaceAreaService.calculate({
            projectId,
            polygonGeojson: geojson,
            planarAreaM2,
        }).then(result => {
            if (surfaceAreaGenRef.current.get(polygonId) !== nextGen)
                return;
            setPolygons(prev => {
                var _a;
                const p = prev.get(polygonId);
                if (!p)
                    return prev;
                // When surface area resolves, swap the on-map label text from
                // planar to surface so the headline value matches LiDAR truth.
                if (((_a = p.labelEntity) === null || _a === void 0 ? void 0 : _a.label) && result.surfaceAreaM2 > 0) {
                    p.labelEntity.label.text = new Cesium.ConstantProperty(formatArea(result.surfaceAreaM2, p.displayUnit));
                }
                const next = new Map(prev);
                next.set(polygonId, {
                    ...p,
                    surfaceAreaLoading: false,
                    surfaceAreaSquareMeters: result.surfaceAreaM2,
                    surfaceAreaCoveredFraction: result.coveredFraction,
                });
                return next;
            });
        }).catch(err => {
            console.warn('[usePolygons] surface area fetch failed:', err);
            if (surfaceAreaGenRef.current.get(polygonId) !== nextGen)
                return;
            setPolygons(prev => {
                const p = prev.get(polygonId);
                if (!p)
                    return prev;
                const next = new Map(prev);
                next.set(polygonId, { ...p, surfaceAreaLoading: false });
                return next;
            });
        });
    }, [projectId, verticesToGeoJSON]);
    // Track Shift so a vertex grab can become a uniform scale. Cleared on blur so
    // a held Shift can't get "stuck" if the window loses focus mid-press.
    useEffect(() => {
        const onKey = (e) => { shiftHeldRef.current = e.shiftKey; };
        const onBlur = () => { shiftHeldRef.current = false; };
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKey);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('keyup', onKey);
            window.removeEventListener('blur', onBlur);
        };
    }, []);
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.removeAll();
            }
            setPolygons(new Map());
            setSelectedPolygon(null);
            setDragging(null);
            activeVertexRef.current = null;
            setActiveVertex(null);
            drawing.clearAll();
            undoStackRef.current = [];
            redoStackRef.current = [];
        }
        prevProjectIdRef.current = projectId;
    }, [projectId, drawing]);
    const createVertexEntity = useCallback((position, color, isFirst) => {
        if (!dataSourceRef.current)
            return null;
        return createPolygonVertex(dataSourceRef.current, position, color, isFirst);
    }, []);
    const createOutlineEntity = useCallback((positions, color, closed, width) => {
        if (!dataSourceRef.current || positions.length < 2)
            return null;
        return createPolygonOutline(dataSourceRef.current, positions, color, closed, 1.0, width, viewer !== null && viewer !== void 0 ? viewer : undefined);
    }, [viewer]);
    const createPolygonEntity = useCallback((positions, color, fillOpacity = DEFAULT_POLYGON_FILL_OPACITY) => {
        if (!dataSourceRef.current || positions.length < 3)
            return null;
        const orderedPositions = createBoundingPolygon(positions);
        return createPolygonFill(dataSourceRef.current, orderedPositions, color, fillOpacity);
    }, []);
    const createLabelEntity = useCallback((positions, area, unit) => {
        if (!dataSourceRef.current || positions.length < 3)
            return null;
        const text = formatArea(area, unit);
        return createPolygonLabel(dataSourceRef.current, positions, text);
    }, []);
    // Phantom-vertex helpers — declared here (before any click/hover handler
    // that references hidePhantom) to keep React-hook ordering valid.
    const ensurePhantomEntity = useCallback(() => {
        if (!dataSourceRef.current)
            return null;
        const existing = phantomNodeRef.current;
        if (existing && dataSourceRef.current.entities.contains(existing)) {
            return existing;
        }
        const phantom = dataSourceRef.current.entities.add({
            show: false,
            position: new Cesium.ConstantPositionProperty(Cesium.Cartesian3.ZERO),
            point: {
                pixelSize: 11,
                color: Cesium.Color.WHITE.withAlpha(0.5),
                outlineColor: Cesium.Color.BLACK.withAlpha(0.6),
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            },
        });
        phantomNodeRef.current = phantom;
        return phantom;
    }, [dataSourceRef]);
    const hidePhantom = useCallback(() => {
        phantomTargetRef.current = null;
        if (phantomNodeRef.current)
            phantomNodeRef.current.show = false;
    }, []);
    // Active-vertex styling. Mutates the point entity directly (no React render
    // round-trip), mirroring the draw hook's applyVertexHighlight/Default.
    const applyVertexActiveStyle = useCallback((entity) => {
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        entity.point.color = new Cesium.ConstantProperty(Cesium.Color.YELLOW);
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(3);
        entity.point.pixelSize = new Cesium.ConstantProperty(14);
    }, []);
    const applyVertexDefaultStyle = useCallback((entity, color, isFirst) => {
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        const base = Cesium.Color.fromCssColorString(color);
        entity.point.color = new Cesium.ConstantProperty(isFirst ? base : base.withAlpha(0.7));
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(2);
        entity.point.pixelSize = new Cesium.ConstantProperty(12);
    }, []);
    // Restore the previously-active vertex (if any) to default styling.
    const defaultPrevActiveVertex = useCallback(() => {
        var _a, _b;
        const prev = activeVertexRef.current;
        if (!prev)
            return;
        const prevPoly = polygonsRef.current.get(prev.polygonId);
        const prevVertex = prevPoly === null || prevPoly === void 0 ? void 0 : prevPoly.vertices.find(v => v.id === prev.vertexId);
        if (prevPoly && prevVertex) {
            const isFirst = ((_a = prevPoly.vertices[0]) === null || _a === void 0 ? void 0 : _a.id) === prevVertex.id;
            applyVertexDefaultStyle(prevVertex.entity, (_b = prevPoly.outlineColor) !== null && _b !== void 0 ? _b : prevPoly.color, isFirst);
        }
    }, [applyVertexDefaultStyle]);
    // Single lockstep setter: default the old active vertex, yellow the new one,
    // keep ref + state in sync. Pass vertexId = null to clear.
    const setActiveVertexById = useCallback((polygonId, vertexId) => {
        defaultPrevActiveVertex();
        if (vertexId) {
            const poly = polygonsRef.current.get(polygonId);
            const vertex = poly === null || poly === void 0 ? void 0 : poly.vertices.find(v => v.id === vertexId);
            applyVertexActiveStyle(vertex === null || vertex === void 0 ? void 0 : vertex.entity);
            activeVertexRef.current = { polygonId, vertexId };
            setActiveVertex({ polygonId, vertexId });
        }
        else {
            activeVertexRef.current = null;
            setActiveVertex(null);
        }
    }, [defaultPrevActiveVertex, applyVertexActiveStyle]);
    const clearActiveVertex = useCallback(() => {
        if (!activeVertexRef.current)
            return;
        defaultPrevActiveVertex();
        activeVertexRef.current = null;
        setActiveVertex(null);
    }, [defaultPrevActiveVertex]);
    useEffect(() => {
        if (!isActive)
            hidePhantom();
    }, [isActive, hidePhantom]);
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    const isPositionInConstraint = useConstraintCheck(constraintPolygon);
    const findNearestEdge = useCallback((polygon, clickPosition) => {
        let minDist = Infinity;
        let nearestEdge = 0;
        for (let i = 0; i < polygon.vertices.length; i++) {
            const j = (i + 1) % polygon.vertices.length;
            const start = polygon.vertices[i].position;
            const end = polygon.vertices[j].position;
            const segDir = Cesium.Cartesian3.subtract(end, start, new Cesium.Cartesian3());
            const segLenSq = Cesium.Cartesian3.dot(segDir, segDir);
            if (segLenSq === 0)
                continue;
            const toClick = Cesium.Cartesian3.subtract(clickPosition, start, new Cesium.Cartesian3());
            const t = Math.max(0, Math.min(1, Cesium.Cartesian3.dot(toClick, segDir) / segLenSq));
            const closestPoint = Cesium.Cartesian3.add(start, Cesium.Cartesian3.multiplyByScalar(segDir, t, new Cesium.Cartesian3()), new Cesium.Cartesian3());
            const dist = Cesium.Cartesian3.distance(clickPosition, closestPoint);
            if (dist < minDist) {
                minDist = dist;
                nearestEdge = i;
            }
        }
        return nearestEdge;
    }, []);
    const insertVertexOnEdge = useCallback((polygonId, edgeIndex, position) => {
        var _a, _b, _c, _d;
        const polygon = polygons.get(polygonId);
        if (!polygon || !dataSourceRef.current)
            return null;
        pushUndoSnapshot();
        const insertIndex = edgeIndex + 1;
        const entity = createVertexEntity(position, (_a = polygon.outlineColor) !== null && _a !== void 0 ? _a : polygon.color, false);
        const newVertex = {
            id: generateVertexId(),
            position,
            entity: entity || undefined
        };
        const newVertices = [...polygon.vertices];
        newVertices.splice(insertIndex, 0, newVertex);
        const newPositions = newVertices.map(v => v.position);
        if ((_b = polygon.outlineEntity) === null || _b === void 0 ? void 0 : _b.polyline) {
            polygon.outlineEntity.polyline.positions = new Cesium.ConstantProperty([...newPositions, newPositions[0]]);
        }
        if ((_c = polygon.polygonEntity) === null || _c === void 0 ? void 0 : _c.polygon) {
            const orderedPositions = createBoundingPolygon(newPositions);
            polygon.polygonEntity.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(orderedPositions));
        }
        const newArea = calculatePolygonArea(newPositions);
        const newPerimeter = calculatePerimeter(newPositions);
        const newEdges = calculateEdges(newPositions);
        if ((_d = polygon.labelEntity) === null || _d === void 0 ? void 0 : _d.label) {
            const center = Cesium.BoundingSphere.fromPoints(newPositions).center;
            polygon.labelEntity.position = center;
            polygon.labelEntity.label.text = new Cesium.ConstantProperty(formatArea(newArea, polygon.displayUnit));
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, {
                ...polygon,
                vertices: newVertices,
                edges: newEdges,
                areaSquareMeters: newArea,
                perimeterMeters: newPerimeter
            });
            return newMap;
        });
        fetchEdgeProfiles(polygonId, newEdges, newPositions);
        fetchSurfaceArea(polygonId, newVertices, newArea);
        return newVertex;
    }, [polygons, createVertexEntity, fetchEdgeProfiles, fetchSurfaceArea]);
    const findPolygonsAtPoint = useCallback((worldPosition) => {
        const cart = Cesium.Cartographic.fromCartesian(worldPosition);
        const point = {
            lat: Cesium.Math.toDegrees(cart.latitude),
            lng: Cesium.Math.toDegrees(cart.longitude)
        };
        const matches = [];
        polygons.forEach((polygon, id) => {
            const vertices = polygon.vertices.map(v => {
                const c = Cesium.Cartographic.fromCartesian(v.position);
                return { lat: Cesium.Math.toDegrees(c.latitude), lng: Cesium.Math.toDegrees(c.longitude) };
            });
            if (isPointInPolygon(point, vertices)) {
                matches.push(id);
            }
        });
        return matches;
    }, [polygons]);
    const checkExistingPolygonClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        // Phantom-vertex click: if the user clicks while the insert-here ghost
        // is visible, treat it as a click on the underlying edge and insert a
        // vertex at the phantom's pinned position.
        if (isActive &&
            (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) &&
            pickedObject.id === phantomNodeRef.current &&
            phantomTargetRef.current) {
            const target = phantomTargetRef.current;
            insertVertexOnEdge(target.polygonId, target.edgeIndex, target.position);
            hidePhantom();
            return true;
        }
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [id, polygon] of polygons.entries()) {
                // During server-artifact edit, only operate on the polygon being edited.
                if (editingPolygonId && id !== editingPolygonId)
                    continue;
                // When tool is active, clicking on outline inserts a vertex
                if (isActive && entity === polygon.outlineEntity) {
                    const position = pickTerrainPosition(event.position);
                    if (position) {
                        const edgeIndex = findNearestEdge(polygon, position);
                        insertVertexOnEdge(id, edgeIndex, position);
                    }
                    return true;
                }
                if (entity === polygon.polygonEntity || entity === polygon.outlineEntity || entity === polygon.labelEntity) {
                    const worldPosition = pickTerrainPosition(event.position);
                    if (worldPosition) {
                        const overlappingIds = findPolygonsAtPoint(worldPosition);
                        if (overlappingIds.length > 1) {
                            setOverlappingPolygons({
                                ids: overlappingIds,
                                position: { x: event.position.x, y: event.position.y }
                            });
                            return true;
                        }
                    }
                    // Clicking the polygon body/outline/label deselects any active
                    // vertex anchor and opens the options dialog.
                    clearActiveVertex();
                    setSelectedPolygon({
                        polygonId: id,
                        screenPosition: { x: event.position.x, y: event.position.y }
                    });
                    return true;
                }
                for (const vertex of polygon.vertices) {
                    if (entity === vertex.entity) {
                        // Toggle the active-vertex anchor (parity with the path tool), but
                        // only while the polygon tool is active — re-clicking the active
                        // vertex clears it (dialog stays as-is); clicking a different vertex
                        // makes it the active anchor and opens the dialog. The anchor and
                        // the dialog are orthogonal. When the tool is inactive, fall back to
                        // the plain "open dialog" behavior.
                        if (isActive) {
                            const prevActive = activeVertexRef.current;
                            if (prevActive && prevActive.polygonId === id && prevActive.vertexId === vertex.id) {
                                setActiveVertexById(id, null);
                                return true;
                            }
                            setActiveVertexById(id, vertex.id);
                        }
                        setSelectedPolygon({
                            polygonId: id,
                            screenPosition: { x: event.position.x, y: event.position.y }
                        });
                        return true;
                    }
                }
            }
        }
        return false;
    }, [viewer, polygons, isActive, pickTerrainPosition, findNearestEdge, insertVertexOnEdge, findPolygonsAtPoint, editingPolygonId, hidePhantom, clearActiveVertex, setActiveVertexById]);
    // Active-node anchor insert (parity with the path tool's "middle-node-selected
    // → insert between selected and next-in-sequence"). When a finished polygon
    // has an active vertex and the user clicks EMPTY terrain, insert a new vertex
    // right after the active one in ring order. "Insert between activeIdx and
    // next" == "insert on edge activeIdx" (the existing edge-insert helper; the
    // closing edge wraps to vertex 0, correctly closing the ring). The inserted
    // vertex becomes the new active anchor so successive clicks chain in order.
    const handleActiveAnchorInsert = useCallback((event) => {
        var _a, _b;
        if (!viewer || viewer.isDestroyed())
            return false;
        const active = activeVertexRef.current;
        if (!active)
            return false;
        // Only act on empty terrain — never when a vertex, outline, ghost, or any
        // entity is under the cursor (those have their own handlers: drag,
        // ghost-insert, outline-insert, vertex-toggle).
        if ((_a = viewer.scene.pick(event.position)) === null || _a === void 0 ? void 0 : _a.id)
            return false;
        const polygon = polygons.get(active.polygonId);
        if (!polygon || polygon.vertices.length < 3)
            return false;
        const activeIdx = polygon.vertices.findIndex(v => v.id === active.vertexId);
        if (activeIdx < 0)
            return false;
        const position = pickTerrainPosition(event.position);
        if (!position)
            return false;
        const prevVertex = polygon.vertices[activeIdx];
        const prevIsFirst = activeIdx === 0;
        const inserted = insertVertexOnEdge(active.polygonId, activeIdx, position);
        if (inserted) {
            // Move the yellow anchor onto the freshly inserted vertex. Styled
            // directly here because the new vertex isn't in polygonsRef until the
            // setPolygons commit lands.
            applyVertexDefaultStyle(prevVertex.entity, (_b = polygon.outlineColor) !== null && _b !== void 0 ? _b : polygon.color, prevIsFirst);
            applyVertexActiveStyle(inserted.entity);
            activeVertexRef.current = { polygonId: active.polygonId, vertexId: inserted.id };
            setActiveVertex({ polygonId: active.polygonId, vertexId: inserted.id });
        }
        return true;
    }, [viewer, polygons, pickTerrainPosition, insertVertexOnEdge, applyVertexDefaultStyle, applyVertexActiveStyle]);
    // Finish the current drawing and create a completed polygon
    const finishPolygon = useCallback((name) => {
        if (drawing.points.length < 3)
            return;
        pushUndoSnapshot();
        const polygonId = generatePolygonId();
        const polygonName = name || nextDefaultPolygonName(polygons);
        // Convert drawing points to Cesium positions
        const positions = drawing.points.map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat));
        // Create vertices with entities
        const vertices = positions.map((pos, idx) => ({
            id: generateVertexId(),
            position: pos,
            entity: createVertexEntity(pos, DEFAULT_POLYGON_COLOR, idx === 0) || undefined
        }));
        const outlineEntity = createOutlineEntity(positions, DEFAULT_POLYGON_COLOR, true);
        const polygonEntity = createPolygonEntity(positions, DEFAULT_POLYGON_COLOR);
        const areaSquareMeters = calculatePolygonArea(positions);
        const perimeterMeters = calculatePerimeter(positions);
        const edges = calculateEdges(positions);
        const newPolygon = {
            id: polygonId,
            name: polygonName,
            vertices,
            edges,
            polygonEntity: polygonEntity || undefined,
            outlineEntity: outlineEntity || undefined,
            labelEntity: undefined,
            areaSquareMeters,
            perimeterMeters,
            color: DEFAULT_POLYGON_COLOR,
            displayUnit: unitPrefs.areaUnit,
            lengthUnit: unitPrefs.distanceUnit,
            projectId: projectId || undefined,
            fillOpacity: DEFAULT_POLYGON_FILL_OPACITY,
            showMeasurements: false
        };
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, newPolygon);
            return newMap;
        });
        fetchEdgeProfiles(polygonId, edges, positions);
        fetchSurfaceArea(polygonId, vertices, areaSquareMeters);
        // Clear the drawing
        drawing.clearAll();
        setSelectedPolygon({
            polygonId,
            screenPosition: { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, [drawing, polygons, unitPrefs.areaUnit, fetchEdgeProfiles, fetchSurfaceArea, createVertexEntity, createOutlineEntity, createPolygonEntity, createLabelEntity]);
    // Keep finishPolygonRef in sync
    useEffect(() => { finishPolygonRef.current = finishPolygon; }, [finishPolygon]);
    // Cancel the current drawing
    const cancelPending = useCallback(() => {
        drawing.clearAll();
    }, [drawing]);
    const handleMapMouseDown = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        // Read-only view of a saved polygon: no vertex drag / move / rotate / scale.
        // Click Edit to modify.
        if (viewLockedRef.current)
            return false;
        // Clear any existing hold timer
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        const pickedObject = viewer.scene.pick(event.position);
        if (!(pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id))
            return false;
        const entity = pickedObject.id;
        // Rotate handle (knob) grab — wins over everything else. Spins the
        // selected polygon around its centroid as the cursor orbits.
        if (entity === rotateHandleEntityRef.current && selectedPolygon) {
            const polygon = polygons.get(selectedPolygon.polygonId);
            const cursor = pickTerrainPosition(event.position);
            if (polygon && cursor) {
                const centroid = Cesium.BoundingSphere.fromPoints(polygon.vertices.map(v => v.position)).center;
                rotateMovedRef.current = false;
                rotationDegRef.current = 0;
                isRotatingRef.current = true;
                setDragging({
                    kind: 'rotate',
                    polygonId: selectedPolygon.polygonId,
                    centroid,
                    grabBearing: enuBearing(centroid, cursor),
                    initialPositions: polygon.vertices.map(v => v.position.clone())
                });
                disableCameraControls(viewer);
                return true;
            }
        }
        for (const [polygonId, polygon] of polygons.entries()) {
            for (let vertexIndex = 0; vertexIndex < polygon.vertices.length; vertexIndex++) {
                const vertex = polygon.vertices[vertexIndex];
                if (entity === vertex.entity) {
                    // Shift + vertex grab → uniform scale about the centroid (whole shape
                    // grows/shrinks, aspect preserved) instead of moving one vertex.
                    if (shiftHeldRef.current && polygon.vertices.length >= 3) {
                        const centroid = Cesium.BoundingSphere.fromPoints(polygon.vertices.map(v => v.position)).center;
                        scaleMovedRef.current = false;
                        setDragging({
                            kind: 'scale',
                            polygonId,
                            centroid,
                            grabRadius: Math.max(Cesium.Cartesian3.distance(vertex.position, centroid), 1e-6),
                            initialPositions: polygon.vertices.map(v => v.position.clone())
                        });
                        disableCameraControls(viewer);
                        return true;
                    }
                    // Plain vertex drag. The old press-and-hold "delete this vertex?"
                    // prompt was removed — deleting a node is now the Delete key or a
                    // right-click, so holding mid-drag no longer interrupts the drag.
                    setDragging({
                        kind: 'vertex',
                        polygonId,
                        vertexId: vertex.id,
                        originalPosition: vertex.position.clone()
                    });
                    mouseDownPosRef.current = { x: event.position.x, y: event.position.y };
                    disableCameraControls(viewer);
                    return true;
                }
            }
        }
        // Whole-polygon translate: grab the fill (or label) to slide the entire
        // shape, every vertex shifted by the same delta. Vertices are matched
        // first above, so grabbing a vertex still reshapes. Enabled when the tool
        // is armed or this polygon's dialog is open (parity with how opened items
        // stay movable), and never while a new polygon is being drawn. The outline
        // is intentionally excluded — it keeps its click-to-insert-vertex role.
        if (drawing.points.length === 0 && !drawing.rectanglePending) {
            for (const [polygonId, polygon] of polygons.entries()) {
                if (editingPolygonId && polygonId !== editingPolygonId)
                    continue;
                const grabbedBody = entity === polygon.polygonEntity || entity === polygon.labelEntity;
                if (!grabbedBody)
                    continue;
                const movable = isActive || (selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId) === polygonId;
                if (!movable)
                    continue;
                const cursor = pickTerrainPosition(event.position);
                if (!cursor)
                    return false;
                translateMovedRef.current = false;
                setDragging({
                    kind: 'translate',
                    polygonId,
                    initialCursor: cursor,
                    initialPositions: polygon.vertices.map(v => v.position.clone())
                });
                disableCameraControls(viewer);
                return true;
            }
        }
        return false;
    }, [viewer, polygons, isActive, selectedPolygon, editingPolygonId, drawing.points.length, drawing.rectanglePending, pickTerrainPosition]);
    const handleMapHover = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return;
        // Cancel hold timer if user moves significantly (they want to drag, not delete)
        if (holdTimerRef.current && mouseDownPosRef.current) {
            const dx = event.endPosition.x - mouseDownPosRef.current.x;
            const dy = event.endPosition.y - mouseDownPosRef.current.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 5) {
                clearTimeout(holdTimerRef.current);
                holdTimerRef.current = null;
            }
        }
        if (dragging) {
            hidePhantom();
            const position = pickTerrainPosition(event.endPosition);
            if (position) {
                if (dragging.kind === 'rotate') {
                    // Cursor only supplies the bearing; constraint doesn't apply.
                    updateRotatedPolygon(position);
                }
                else if (dragging.kind === 'scale') {
                    // Cursor only supplies the scale factor; constraint doesn't apply.
                    updateScaledPolygon(position);
                }
                else if (isPositionInConstraint(position)) {
                    if (dragging.kind === 'translate') {
                        updateTranslatedPolygon(position);
                    }
                    else {
                        updateDraggedVertex(position);
                    }
                }
            }
            return;
        }
        // Edge hover detection: check if hovering over an outline entity. When
        // an outline is hit AND the tool is active, also paint the phantom
        // vertex at the cursor's terrain projection (matches path tool).
        const pickedObject = viewer.scene.pick(event.endPosition);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [polygonId, polygon] of polygons.entries()) {
                if (entity === polygon.outlineEntity && polygon.edges) {
                    const position = pickTerrainPosition(event.endPosition);
                    if (position) {
                        const edgeIndex = findNearestEdge(polygon, position);
                        const edge = polygon.edges[edgeIndex];
                        if (edge) {
                            setHoveredEdge({
                                polygonId,
                                edgeIndex,
                                distanceMeters: edge.distanceMeters,
                                screenPosition: { x: event.endPosition.x, y: event.endPosition.y }
                            });
                            if (isActive) {
                                const phantom = ensurePhantomEntity();
                                if (phantom) {
                                    phantom.position = new Cesium.ConstantPositionProperty(position);
                                    if (phantom.point) {
                                        phantom.point.color = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(polygon.color).withAlpha(0.55));
                                    }
                                    phantom.show = true;
                                    phantomTargetRef.current = { polygonId, edgeIndex, position };
                                }
                            }
                            else {
                                hidePhantom();
                            }
                            return;
                        }
                    }
                }
            }
        }
        if (hoveredEdge) {
            setHoveredEdge(null);
        }
        hidePhantom();
    }, [viewer, dragging, polygons, hoveredEdge, pickTerrainPosition, isPositionInConstraint, findNearestEdge, isActive, ensurePhantomEntity, hidePhantom]);
    const updateDraggedVertex = useCallback((newPosition) => {
        var _a;
        if (!dragging)
            return;
        const polygon = polygons.get(dragging.polygonId);
        if (!polygon)
            return;
        const vertexIndex = polygon.vertices.findIndex(v => v.id === dragging.vertexId);
        if (vertexIndex === -1)
            return;
        const vertex = polygon.vertices[vertexIndex];
        if (vertex.entity) {
            vertex.entity.position = new Cesium.ConstantPositionProperty(newPosition);
        }
        const newPositions = polygon.vertices.map((v, i) => i === vertexIndex ? newPosition : v.position);
        const newArea = calculatePolygonArea(newPositions);
        if ((_a = polygon.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            const center = Cesium.BoundingSphere.fromPoints(newPositions).center;
            polygon.labelEntity.position = center;
            polygon.labelEntity.label.text = new Cesium.ConstantProperty(formatArea(newArea, polygon.displayUnit));
        }
    }, [dragging, polygons]);
    // Whole-polygon translate: shift every vertex entity by the same lon/lat
    // delta (cursor − grab anchor), preserving vertex height. The outline and
    // fill follow live via the drag CallbackProperty effect below (which reads
    // each vertex entity's live position), so only the points + label move here.
    // Area is unchanged by a pure translation, so the label text stays put.
    const updateTranslatedPolygon = useCallback((cursor) => {
        var _a;
        if (!dragging || dragging.kind !== 'translate' || !dragging.initialCursor || !dragging.initialPositions)
            return;
        const polygon = polygons.get(dragging.polygonId);
        if (!polygon)
            return;
        const start = Cesium.Cartographic.fromCartesian(dragging.initialCursor);
        const now = Cesium.Cartographic.fromCartesian(cursor);
        const dLon = now.longitude - start.longitude;
        const dLat = now.latitude - start.latitude;
        if (dLon !== 0 || dLat !== 0)
            translateMovedRef.current = true;
        polygon.vertices.forEach((vertex, i) => {
            const orig = dragging.initialPositions[i];
            if (!orig || !vertex.entity)
                return;
            const oc = Cesium.Cartographic.fromCartesian(orig);
            const moved = Cesium.Cartesian3.fromRadians(oc.longitude + dLon, oc.latitude + dLat, oc.height);
            vertex.entity.position = new Cesium.ConstantPositionProperty(moved);
        });
        if ((_a = polygon.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            const livePositions = polygon.vertices.map(v => { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = v.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, Cesium.JulianDate.now())) !== null && _d !== void 0 ? _d : v.position; });
            const center = Cesium.BoundingSphere.fromPoints(livePositions).center;
            polygon.labelEntity.position = center;
        }
    }, [dragging, polygons]);
    // Rotate: spin every vertex around the (gesture-fixed) centroid by the angle
    // swept since the grab, in the local ENU frame so it doesn't distort with
    // latitude. The knob follows the cursor (handleBearingRef = current bearing).
    const updateRotatedPolygon = useCallback((cursor) => {
        if (!dragging || dragging.kind !== 'rotate' || !dragging.centroid ||
            dragging.grabBearing === undefined || !dragging.initialPositions)
            return;
        const polygon = polygons.get(dragging.polygonId);
        if (!polygon)
            return;
        const C = dragging.centroid;
        const curBearing = enuBearing(C, cursor);
        const delta = curBearing - dragging.grabBearing;
        if (delta !== 0)
            rotateMovedRef.current = true;
        rotationDegRef.current = normalizeAngleDeg(delta);
        polygon.vertices.forEach((vertex, i) => {
            const orig = dragging.initialPositions[i];
            if (!orig || !vertex.entity)
                return;
            vertex.entity.position = new Cesium.ConstantPositionProperty(rotateAroundCentroid(C, orig, delta));
        });
        handleBearingRef.current = curBearing;
    }, [dragging, polygons]);
    // Uniform scale: every vertex scales about the (gesture-fixed) centroid by the
    // factor = current cursor distance / the dragged vertex's original distance.
    // Shape + aspect ratio preserved; clamped so it can't collapse to nothing.
    const updateScaledPolygon = useCallback((cursor) => {
        if (!dragging || dragging.kind !== 'scale' || !dragging.centroid ||
            dragging.grabRadius === undefined || !dragging.initialPositions)
            return;
        const polygon = polygons.get(dragging.polygonId);
        if (!polygon)
            return;
        const C = dragging.centroid;
        const factor = Math.max(Cesium.Cartesian3.distance(cursor, C) / dragging.grabRadius, 0.02);
        if (factor !== 1)
            scaleMovedRef.current = true;
        polygon.vertices.forEach((vertex, i) => {
            const orig = dragging.initialPositions[i];
            if (!orig || !vertex.entity)
                return;
            vertex.entity.position = new Cesium.ConstantPositionProperty(scaleAroundCentroid(C, orig, factor));
        });
    }, [dragging, polygons]);
    // Manage the rotate handle (slim stem + small knob) and the live angle pill
    // for the selected polygon while the tool is armed. Geometry is
    // CallbackProperty-driven so it tracks the live centroid + knob bearing during
    // a rotate. Keyed only on selection identity + armed state so routine
    // `polygons` mutations don't reset the knob bearing.
    useEffect(() => {
        const ds = dataSourceRef.current;
        if (!ds)
            return;
        const removeHandles = () => {
            if (rotateStemEntityRef.current) {
                ds.entities.remove(rotateStemEntityRef.current);
                rotateStemEntityRef.current = null;
            }
            if (rotateHandleEntityRef.current) {
                ds.entities.remove(rotateHandleEntityRef.current);
                rotateHandleEntityRef.current = null;
            }
            if (anglePillEntityRef.current) {
                ds.entities.remove(anglePillEntityRef.current);
                anglePillEntityRef.current = null;
            }
        };
        const pid = selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId;
        const poly = pid ? polygonsRef.current.get(pid) : undefined;
        if (!pid || !poly || !isActive || poly.vertices.length < 3) {
            removeHandles();
            return;
        }
        // New selection → reset the knob to due north.
        handleBearingRef.current = 0;
        const centroidWorld = () => {
            const p = polygonsRef.current.get(pid);
            return p ? Cesium.BoundingSphere.fromPoints(livePositionsOf(p)).center : Cesium.Cartesian3.ZERO;
        };
        // Knob sits a fixed screen-pixel gap beyond where the stem exits the polygon
        // outline, so it hugs the shape regardless of zoom or polygon size.
        const handleWorld = () => {
            var _a;
            const p = polygonsRef.current.get(pid);
            if (!p || !viewer || viewer.isDestroyed())
                return Cesium.Cartesian3.ZERO;
            const pts = livePositionsOf(p);
            const sphere = Cesium.BoundingSphere.fromPoints(pts);
            const start = (_a = boundaryPointAlongBearing(sphere.center, handleBearingRef.current, pts)) !== null && _a !== void 0 ? _a : sphere.center;
            return offsetAlongBearing(start, handleBearingRef.current, rotateGapMeters(viewer, sphere));
        };
        // Slim forest stem — starts at the polygon perimeter (not the centre) and
        // runs out to the knob.
        rotateStemEntityRef.current = ds.entities.add({
            polyline: {
                positions: new Cesium.CallbackProperty(() => {
                    var _a;
                    const p = polygonsRef.current.get(pid);
                    if (!p)
                        return [];
                    const pts = livePositionsOf(p);
                    const C = Cesium.BoundingSphere.fromPoints(pts).center;
                    const start = (_a = boundaryPointAlongBearing(C, handleBearingRef.current, pts)) !== null && _a !== void 0 ? _a : C;
                    return [start, handleWorld()];
                }, false),
                width: 1.5,
                material: new Cesium.ColorMaterialProperty(CHROME_FOREST.withAlpha(0.85)),
            },
        });
        // Small white knob with a thin forest ring.
        rotateHandleEntityRef.current = ds.entities.add({
            position: new Cesium.CallbackProperty(() => handleWorld(), false),
            point: {
                pixelSize: 10,
                color: CHROME_SAND,
                outlineColor: CHROME_FOREST,
                outlineWidth: 1.5,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        // Live angle readout pill — sand bg, forest text — shown only while rotating,
        // floating just above the knob.
        anglePillEntityRef.current = ds.entities.add({
            position: new Cesium.CallbackProperty(() => handleWorld(), false),
            label: {
                text: new Cesium.CallbackProperty(() => `${rotationDegRef.current}°`, false),
                show: new Cesium.CallbackProperty(() => isRotatingRef.current, false),
                font: '600 12px -apple-system, system-ui, sans-serif',
                fillColor: CHROME_FOREST,
                showBackground: true,
                backgroundColor: CHROME_SAND.withAlpha(0.95),
                backgroundPadding: new Cesium.Cartesian2(8, 4),
                style: Cesium.LabelStyle.FILL,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                pixelOffset: new Cesium.Cartesian2(0, -14),
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
        });
        return removeHandles;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId, isActive]);
    // Vertex handles are shown only while a polygon is actively editable: it's the
    // selected polygon AND not in read-only view mode. Every other finished polygon
    // — deselected, or view-locked — reads as a clean shape with no handles (the
    // rotate knob is already suppressed via isActive). Fill/outline are never
    // touched here, so the polygon stays visible regardless.
    useEffect(() => {
        const selId = selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId;
        polygons.forEach((poly, id) => {
            const showHandles = id === selId && !viewLocked;
            poly.vertices.forEach(v => { if (v.entity)
                v.entity.show = showHandles; });
        });
    }, [viewLocked, selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId, polygons]);
    // Persistent measurement labels — per-side length · azimuth plus the centroid
    // area — for every polygon whose showMeasurements flag is on (toggled from the
    // options dialog). Pinned independent of selection, like the measure tool's
    // labels. Positions + text are CallbackProperty-driven so they follow live
    // moves / rotations / scales without a rebuild; the effect only rebuilds when
    // the set of flagged polygons (or their vertex counts) changes.
    const measurementSig = Array.from(polygons.values())
        .filter(p => p.showMeasurements && p.vertices.length >= 3)
        .map(p => `${p.id}:${p.vertices.length}`)
        .join('|');
    useEffect(() => {
        const ds = dataSourceRef.current;
        if (!ds)
            return;
        const removeLabels = () => {
            edgeLabelEntitiesRef.current.forEach(e => ds.entities.remove(e));
            edgeLabelEntitiesRef.current = [];
        };
        removeLabels();
        const flagged = Array.from(polygonsRef.current.values())
            .filter(p => p.showMeasurements && p.vertices.length >= 3);
        for (const poly of flagged) {
            const pid = poly.id;
            const n = poly.vertices.length;
            for (let i = 0; i < n; i++) {
                const a = i;
                const b = (i + 1) % n;
                const labelPos = () => {
                    const p = polygonsRef.current.get(pid);
                    if (!p)
                        return Cesium.Cartesian3.ZERO;
                    const pts = livePositionsOf(p);
                    return Cesium.Cartesian3.midpoint(pts[a], pts[b], new Cesium.Cartesian3());
                };
                const labelText = () => {
                    var _a;
                    const p = polygonsRef.current.get(pid);
                    if (!p)
                        return '';
                    const pts = livePositionsOf(p);
                    const dist = Cesium.Cartesian3.distance(pts[a], pts[b]);
                    // Edge lengths use the polygon's own length unit (independent of the
                    // area unit). CallbackProperty reads live, so changing the unit
                    // re-stamps the labels on the next frame without a rebuild. Falls back
                    // to deriving ft/m from the area unit for older polygons w/o lengthUnit.
                    const lengthUnit = (_a = p.lengthUnit) !== null && _a !== void 0 ? _a : ((p.displayUnit === 'sqft' || p.displayUnit === 'acres' || p.displayUnit === 'sqyd' || p.displayUnit === 'sqmi') ? 'ft' : 'm');
                    return `${formatDistance(dist, lengthUnit)} · ${edgeAzimuthDeg(pts[a], pts[b])}°`;
                };
                // Hide the label when its edge is too short on screen, so they stop
                // piling up when zoomed out (worst on small rectangles).
                const labelVisible = () => {
                    if (!viewer || viewer.isDestroyed())
                        return false;
                    const p = polygonsRef.current.get(pid);
                    if (!p)
                        return false;
                    const pts = livePositionsOf(p);
                    const s1 = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pts[a]);
                    const s2 = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pts[b]);
                    if (!s1 || !s2)
                        return false;
                    const dx = s1.x - s2.x;
                    const dy = s1.y - s2.y;
                    return Math.sqrt(dx * dx + dy * dy) >= 56;
                };
                // Push the edge label outward from the polygon centre (in screen space)
                // so it hugs its edge instead of drifting into the middle, where it
                // would collide with the centroid area label on squat shapes (e.g. a
                // flat triangle whose top-edge midpoint sits right on the centre).
                const labelOffset = () => {
                    if (!viewer || viewer.isDestroyed())
                        return Cesium.Cartesian2.ZERO;
                    const p = polygonsRef.current.get(pid);
                    if (!p)
                        return Cesium.Cartesian2.ZERO;
                    const pts = livePositionsOf(p);
                    const mid = Cesium.Cartesian3.midpoint(pts[a], pts[b], new Cesium.Cartesian3());
                    const centre = Cesium.BoundingSphere.fromPoints(pts).center;
                    const sMid = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, mid);
                    const sCentre = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, centre);
                    if (!sMid || !sCentre)
                        return Cesium.Cartesian2.ZERO;
                    const dx = sMid.x - sCentre.x;
                    const dy = sMid.y - sCentre.y;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    if (len < 1)
                        return Cesium.Cartesian2.ZERO;
                    const push = 22;
                    return new Cesium.Cartesian2((dx / len) * push, (dy / len) * push);
                };
                edgeLabelEntitiesRef.current.push(ds.entities.add({
                    position: new Cesium.CallbackProperty(() => labelPos(), false),
                    label: {
                        text: new Cesium.CallbackProperty(() => labelText(), false),
                        show: new Cesium.CallbackProperty(() => labelVisible(), false),
                        pixelOffset: new Cesium.CallbackProperty(() => labelOffset(), false),
                        font: '600 11px -apple-system, system-ui, sans-serif',
                        fillColor: CHROME_FOREST,
                        showBackground: true,
                        backgroundColor: CHROME_SAND.withAlpha(0.92),
                        backgroundPadding: new Cesium.Cartesian2(7, 4),
                        style: Cesium.LabelStyle.FILL,
                        verticalOrigin: Cesium.VerticalOrigin.CENTER,
                        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    },
                }));
            }
            // Centroid area label — the polygon's area in its display unit, pinned in
            // the middle of the shape.
            const areaPos = () => {
                const p = polygonsRef.current.get(pid);
                if (!p)
                    return Cesium.Cartesian3.ZERO;
                return Cesium.BoundingSphere.fromPoints(livePositionsOf(p)).center;
            };
            const areaText = () => {
                const p = polygonsRef.current.get(pid);
                if (!p)
                    return '';
                return formatArea(calculatePolygonArea(livePositionsOf(p)), p.displayUnit);
            };
            edgeLabelEntitiesRef.current.push(ds.entities.add({
                position: new Cesium.CallbackProperty(() => areaPos(), false),
                label: {
                    text: new Cesium.CallbackProperty(() => areaText(), false),
                    font: '700 12px -apple-system, system-ui, sans-serif',
                    fillColor: CHROME_FOREST,
                    showBackground: true,
                    backgroundColor: CHROME_SAND.withAlpha(0.92),
                    backgroundPadding: new Cesium.Cartesian2(8, 5),
                    style: Cesium.LabelStyle.FILL,
                    verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                },
            }));
        }
        return removeLabels;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measurementSig]);
    // While a polygon vertex is being dragged, swap the outline polyline.positions and
    // polygon.hierarchy to CallbackProperties so they track the cursor live (clampToGround
    // primitives need per-frame re-evaluation to follow smoothly).
    useEffect(() => {
        var _a, _b;
        if (!dragging)
            return;
        const polygon = polygons.get(dragging.polygonId);
        if (!polygon)
            return;
        const vertices = polygon.vertices;
        const livePositions = () => {
            const now = Cesium.JulianDate.now();
            return vertices.map(v => { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = v.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, now)) !== null && _d !== void 0 ? _d : v.position; });
        };
        if ((_a = polygon.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
            polygon.outlineEntity.polyline.positions = new Cesium.CallbackProperty(() => {
                const positions = livePositions();
                return [...positions, positions[0]];
            }, false);
        }
        if ((_b = polygon.polygonEntity) === null || _b === void 0 ? void 0 : _b.polygon) {
            polygon.polygonEntity.polygon.hierarchy = new Cesium.CallbackProperty(() => {
                const ordered = createBoundingPolygon(livePositions());
                return new Cesium.PolygonHierarchy(ordered);
            }, false);
        }
        return () => {
            var _a, _b;
            const positions = livePositions();
            if ((_a = polygon.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                polygon.outlineEntity.polyline.positions = new Cesium.ConstantProperty([...positions, positions[0]]);
            }
            if ((_b = polygon.polygonEntity) === null || _b === void 0 ? void 0 : _b.polygon) {
                const ordered = createBoundingPolygon(positions);
                polygon.polygonEntity.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(ordered));
            }
        };
    }, [dragging, polygons]);
    const handleMapMouseUp = useCallback(() => {
        var _a, _b, _c;
        // Clear hold timer
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        mouseDownPosRef.current = null;
        if (!dragging || !viewer)
            return;
        enableCameraControls(viewer);
        isRotatingRef.current = false; // hide the angle pill
        // Whole-polygon translate/rotate/scale: commit the moved vertex positions
        // (read live from each vertex entity) back into state, then recompute
        // edges/perimeter. (Scale changes the area; translate/rotate preserve it.)
        // Edge profiles + LiDAR surface area depend on the new geometry, so refetch
        // both. A grab without movement skips the commit + refetch.
        if (dragging.kind === 'translate' || dragging.kind === 'rotate' || dragging.kind === 'scale') {
            const didMove = dragging.kind === 'translate' ? translateMovedRef.current
                : dragging.kind === 'rotate' ? rotateMovedRef.current
                    : scaleMovedRef.current;
            if (!didMove) {
                setDragging(null);
                return;
            }
            pushUndoSnapshot();
            const tid = dragging.polygonId;
            const polygon = polygons.get(tid);
            if (polygon) {
                const updatedVertices = polygon.vertices.map(v => {
                    var _a, _b, _c;
                    const moved = (_c = (_b = (_a = v.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, Cesium.JulianDate.now());
                    return moved ? { ...v, position: moved } : v;
                });
                const positions = updatedVertices.map(v => v.position);
                const newEdges = calculateEdges(positions);
                const newArea = calculatePolygonArea(positions);
                const newPerimeter = calculatePerimeter(positions);
                setPolygons(prev => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(tid);
                    if (existing) {
                        newMap.set(tid, {
                            ...existing,
                            vertices: updatedVertices,
                            edges: newEdges,
                            areaSquareMeters: newArea,
                            perimeterMeters: newPerimeter
                        });
                    }
                    return newMap;
                });
                fetchEdgeProfiles(tid, newEdges, positions);
                fetchSurfaceArea(tid, updatedVertices, newArea);
            }
            setDragging(null);
            return;
        }
        const polygon = polygons.get(dragging.polygonId);
        if (polygon) {
            const vertexIndex = polygon.vertices.findIndex(v => v.id === dragging.vertexId);
            if (vertexIndex !== -1) {
                const newPosition = (_c = (_b = (_a = polygon.vertices[vertexIndex].entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, Cesium.JulianDate.now());
                // Only record an undo step if the vertex actually moved (a click
                // without a drag commits the same position — no edit to undo).
                const movedVertex = !!newPosition && !!dragging.originalPosition &&
                    Cesium.Cartesian3.distance(newPosition, dragging.originalPosition) > 0.01;
                if (newPosition) {
                    if (movedVertex)
                        pushUndoSnapshot();
                    const dragPolygonId = dragging.polygonId;
                    const updatedVertices = [...polygon.vertices];
                    updatedVertices[vertexIndex] = { ...updatedVertices[vertexIndex], position: newPosition };
                    const positions = updatedVertices.map(v => v.position);
                    const newEdges = calculateEdges(positions);
                    const dragNewArea = calculatePolygonArea(positions);
                    setPolygons(prev => {
                        const newMap = new Map(prev);
                        const existingPolygon = newMap.get(dragPolygonId);
                        if (existingPolygon) {
                            const newPerimeter = calculatePerimeter(positions);
                            newMap.set(dragPolygonId, {
                                ...existingPolygon,
                                vertices: updatedVertices,
                                edges: newEdges,
                                areaSquareMeters: dragNewArea,
                                perimeterMeters: newPerimeter
                            });
                        }
                        return newMap;
                    });
                    fetchEdgeProfiles(dragPolygonId, newEdges, positions);
                    fetchSurfaceArea(dragPolygonId, updatedVertices, dragNewArea);
                }
            }
        }
        setDragging(null);
    }, [dragging, viewer, polygons, fetchEdgeProfiles, fetchSurfaceArea]);
    const removePolygon = useCallback((polygonId) => {
        var _a;
        const polygon = polygons.get(polygonId);
        if (!polygon || !dataSourceRef.current)
            return;
        pushUndoSnapshot();
        polygon.vertices.forEach(vertex => {
            var _a;
            if (vertex.entity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(vertex.entity);
            }
        });
        if (polygon.outlineEntity) {
            dataSourceRef.current.entities.remove(polygon.outlineEntity);
        }
        if (polygon.polygonEntity) {
            dataSourceRef.current.entities.remove(polygon.polygonEntity);
        }
        if (polygon.labelEntity) {
            dataSourceRef.current.entities.remove(polygon.labelEntity);
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.delete(polygonId);
            return newMap;
        });
        if ((selectedPolygon === null || selectedPolygon === void 0 ? void 0 : selectedPolygon.polygonId) === polygonId) {
            setSelectedPolygon(null);
        }
        if (((_a = activeVertexRef.current) === null || _a === void 0 ? void 0 : _a.polygonId) === polygonId) {
            activeVertexRef.current = null;
            setActiveVertex(null);
        }
    }, [polygons, selectedPolygon]);
    const deleteVertex = useCallback((polygonId, vertexIndex) => {
        var _a, _b, _c, _d, _f;
        const polygon = polygons.get(polygonId);
        if (!polygon || !dataSourceRef.current)
            return;
        // Can't delete if polygon would have less than 3 vertices
        if (polygon.vertices.length <= 3) {
            // Just remove the entire polygon instead (removePolygon snapshots).
            removePolygon(polygonId);
            return;
        }
        pushUndoSnapshot();
        const vertexToDelete = polygon.vertices[vertexIndex];
        // Clear the active anchor if it pointed at the vertex being deleted.
        if (((_a = activeVertexRef.current) === null || _a === void 0 ? void 0 : _a.polygonId) === polygonId && activeVertexRef.current.vertexId === (vertexToDelete === null || vertexToDelete === void 0 ? void 0 : vertexToDelete.id)) {
            activeVertexRef.current = null;
            setActiveVertex(null);
        }
        // Remove the vertex entity
        if (vertexToDelete.entity) {
            dataSourceRef.current.entities.remove(vertexToDelete.entity);
        }
        // Build new vertices array without the deleted vertex
        const newVertices = polygon.vertices.filter((_, idx) => idx !== vertexIndex);
        // If we deleted the first vertex, update the new first vertex to be the start point style
        if (vertexIndex === 0) {
            const newFirstVertex = newVertices[0];
            if (newFirstVertex.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(newFirstVertex.entity);
                newFirstVertex.entity = createVertexEntity(newFirstVertex.position, (_b = polygon.outlineColor) !== null && _b !== void 0 ? _b : polygon.color, true) || undefined;
            }
        }
        // Get new positions
        const newPositions = newVertices.map(v => v.position);
        // Update outline
        if ((_c = polygon.outlineEntity) === null || _c === void 0 ? void 0 : _c.polyline) {
            polygon.outlineEntity.polyline.positions = new Cesium.ConstantProperty([...newPositions, newPositions[0]]);
        }
        // Update polygon fill
        if ((_d = polygon.polygonEntity) === null || _d === void 0 ? void 0 : _d.polygon) {
            // Use createBoundingPolygon to handle self-intersecting polygons
            const orderedPositions = createBoundingPolygon(newPositions);
            polygon.polygonEntity.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(orderedPositions));
        }
        // Calculate new area, perimeter and edges
        const newArea = calculatePolygonArea(newPositions);
        const newPerimeter = calculatePerimeter(newPositions);
        const newEdges = calculateEdges(newPositions);
        // Update label
        if ((_f = polygon.labelEntity) === null || _f === void 0 ? void 0 : _f.label) {
            const center = Cesium.BoundingSphere.fromPoints(newPositions).center;
            polygon.labelEntity.position = center;
            polygon.labelEntity.label.text = new Cesium.ConstantProperty(formatArea(newArea, polygon.displayUnit));
        }
        // Update state
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, {
                ...polygon,
                vertices: newVertices,
                edges: newEdges,
                areaSquareMeters: newArea,
                perimeterMeters: newPerimeter
            });
            return newMap;
        });
        fetchEdgeProfiles(polygonId, newEdges, newPositions);
        fetchSurfaceArea(polygonId, newVertices, newArea);
        setDeleteVertexPrompt(null);
    }, [polygons, removePolygon, createVertexEntity, fetchEdgeProfiles, fetchSurfaceArea]);
    // Keep the drawing entity click handler ref in sync
    useEffect(() => {
        handleDrawingEntityClickRef.current = (entity, screenPosition) => {
            if (!viewer || viewer.isDestroyed())
                return false;
            for (const [polygonId, polygon] of polygons.entries()) {
                if (entity === polygon.outlineEntity) {
                    const position = pickTerrainPosition(screenPosition);
                    if (!position)
                        return false;
                    const edgeIndex = findNearestEdge(polygon, position);
                    insertVertexOnEdge(polygonId, edgeIndex, position);
                    return true;
                }
            }
            return false;
        };
    }, [viewer, polygons, pickTerrainPosition, findNearestEdge, insertVertexOnEdge]);
    const confirmDeleteVertex = useCallback(() => {
        if (deleteVertexPrompt) {
            deleteVertex(deleteVertexPrompt.polygonId, deleteVertexPrompt.vertexIndex);
        }
    }, [deleteVertexPrompt, deleteVertex]);
    const cancelDeleteVertex = useCallback(() => {
        setDeleteVertexPrompt(null);
    }, []);
    const updatePolygonColor = useCallback((polygonId, color) => {
        var _a, _b, _c;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        const outlineTracksFill = polygon.outlineColor === undefined;
        if (outlineTracksFill) {
            polygon.vertices.forEach((vertex, idx) => {
                var _a;
                if ((_a = vertex.entity) === null || _a === void 0 ? void 0 : _a.point) {
                    const isFirst = idx === 0;
                    vertex.entity.point.color = new Cesium.ConstantProperty(isFirst
                        ? Cesium.Color.fromCssColorString(color)
                        : Cesium.Color.fromCssColorString(color).withAlpha(0.7));
                }
            });
            if ((_a = polygon.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                polygon.outlineEntity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color));
            }
        }
        if ((_b = polygon.polygonEntity) === null || _b === void 0 ? void 0 : _b.polygon) {
            const alpha = (_c = polygon.fillOpacity) !== null && _c !== void 0 ? _c : DEFAULT_POLYGON_FILL_OPACITY;
            polygon.polygonEntity.polygon.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(alpha));
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, { ...polygon, color });
            return newMap;
        });
    }, [polygons]);
    const updatePolygonOutlineColor = useCallback((polygonId, color) => {
        var _a;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        polygon.vertices.forEach((vertex, idx) => {
            var _a;
            if ((_a = vertex.entity) === null || _a === void 0 ? void 0 : _a.point) {
                const isFirst = idx === 0;
                vertex.entity.point.color = new Cesium.ConstantProperty(isFirst
                    ? Cesium.Color.fromCssColorString(color)
                    : Cesium.Color.fromCssColorString(color).withAlpha(0.7));
            }
        });
        if ((_a = polygon.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
            polygon.outlineEntity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color));
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(polygonId);
            if (existing)
                newMap.set(polygonId, { ...existing, outlineColor: color });
            return newMap;
        });
    }, [polygons]);
    const updatePolygonFillOpacity = useCallback((polygonId, opacity) => {
        var _a;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        if ((_a = polygon.polygonEntity) === null || _a === void 0 ? void 0 : _a.polygon) {
            polygon.polygonEntity.polygon.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(polygon.color).withAlpha(opacity));
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(polygonId);
            if (existing)
                newMap.set(polygonId, { ...existing, fillOpacity: opacity });
            return newMap;
        });
    }, [polygons]);
    const updatePolygonLineWidth = useCallback((polygonId, width) => {
        var _a;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        if ((_a = polygon.outlineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
            polygon.outlineEntity.polyline.width = viewer
                ? makePolylineWidthProperty(width, viewer)
                : new Cesium.ConstantProperty(width);
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(polygonId);
            if (existing)
                newMap.set(polygonId, { ...existing, lineWidth: width });
            return newMap;
        });
    }, [polygons, viewer]);
    const updatePolygonName = useCallback((polygonId, name) => {
        setPolygons(prev => {
            const newMap = new Map(prev);
            const polygon = newMap.get(polygonId);
            if (polygon) {
                newMap.set(polygonId, { ...polygon, name });
            }
            return newMap;
        });
    }, []);
    // Pin/unpin the polygon's on-map measurements (edge length·azimuth + centroid
    // area). The label-rendering effect reacts to this flag; persistence picks it
    // up via the polygons map.
    const setPolygonShowMeasurements = useCallback((polygonId, show) => {
        setPolygons(prev => {
            const newMap = new Map(prev);
            const polygon = newMap.get(polygonId);
            if (polygon) {
                newMap.set(polygonId, { ...polygon, showMeasurements: show });
            }
            return newMap;
        });
    }, []);
    const updatePolygonUnit = useCallback((polygonId, unit) => {
        var _a;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        if ((_a = polygon.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            polygon.labelEntity.label.text = new Cesium.ConstantProperty(formatArea(polygon.areaSquareMeters, unit));
        }
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, { ...polygon, displayUnit: unit });
            return newMap;
        });
        setDefaultUnit(unit);
    }, [polygons]);
    // Independent length unit for this polygon's perimeter + edge labels. The
    // edge labels read it live (CallbackProperty), so they re-stamp on change.
    const setPolygonLengthUnit = useCallback((polygonId, unit) => {
        setPolygons(prev => {
            const newMap = new Map(prev);
            const polygon = newMap.get(polygonId);
            if (polygon)
                newMap.set(polygonId, { ...polygon, lengthUnit: unit });
            return newMap;
        });
    }, []);
    const updatePolygonOpacity = useCallback((polygonId, opacity) => {
        var _a, _b, _c;
        const polygon = polygons.get(polygonId);
        if (!polygon)
            return;
        const baseColor = Cesium.Color.fromCssColorString(polygon.color);
        if ((_a = polygon.polygonEntity) === null || _a === void 0 ? void 0 : _a.polygon) {
            polygon.polygonEntity.polygon.material = new Cesium.ColorMaterialProperty(baseColor.withAlpha(opacity * 0.3));
        }
        if ((_b = polygon.outlineEntity) === null || _b === void 0 ? void 0 : _b.polyline) {
            polygon.outlineEntity.polyline.material = new Cesium.ColorMaterialProperty(baseColor.withAlpha(opacity));
        }
        polygon.vertices.forEach((vertex, idx) => {
            var _a;
            if ((_a = vertex.entity) === null || _a === void 0 ? void 0 : _a.point) {
                const isFirst = idx === 0;
                vertex.entity.point.color = new Cesium.ConstantProperty(isFirst
                    ? baseColor.withAlpha(opacity)
                    : baseColor.withAlpha(opacity * 0.7));
                vertex.entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            }
        });
        if ((_c = polygon.labelEntity) === null || _c === void 0 ? void 0 : _c.label) {
            polygon.labelEntity.label.fillColor = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(opacity));
            polygon.labelEntity.label.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
        }
        setItemOpacities(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, opacity);
            return newMap;
        });
    }, [polygons]);
    const getItemOpacity = (id) => {
        var _a;
        return (_a = itemOpacities.get(id)) !== null && _a !== void 0 ? _a : 1;
    };
    const highlightEdge = useCallback((polygonId, edgeIndex) => {
        if (edgeGlowEntityRef.current && dataSourceRef.current) {
            dataSourceRef.current.entities.remove(edgeGlowEntityRef.current);
            edgeGlowEntityRef.current = null;
        }
        if (edgeIndex === null)
            return;
        const polygon = polygons.get(polygonId);
        if (!polygon || !dataSourceRef.current)
            return;
        const startVertex = polygon.vertices[edgeIndex];
        const endVertex = polygon.vertices[(edgeIndex + 1) % polygon.vertices.length];
        if (!startVertex || !endVertex)
            return;
        edgeGlowEntityRef.current = createGlowHighlight(dataSourceRef.current, [startVertex.position, endVertex.position], polygon.color);
    }, [polygons]);
    const dismissSelection = useCallback(() => {
        setSelectedPolygon(null);
        clearActiveVertex();
    }, [clearActiveVertex]);
    const selectPolygonById = useCallback((polygonId, screenPosition) => {
        // No existence check: caller may invoke right after a state-updating create.
        setSelectedPolygon({
            polygonId,
            screenPosition: screenPosition !== null && screenPosition !== void 0 ? screenPosition : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, []);
    const handleOverlapSelect = useCallback((polygonId) => {
        setOverlappingPolygons(null);
        const position = (overlappingPolygons === null || overlappingPolygons === void 0 ? void 0 : overlappingPolygons.position) || { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setSelectedPolygon({
            polygonId,
            screenPosition: position
        });
    }, [overlappingPolygons]);
    const dismissOverlapPicker = useCallback(() => {
        setOverlappingPolygons(null);
    }, []);
    const getOverlapSpiderItems = useCallback(() => {
        if (!overlappingPolygons)
            return [];
        return overlappingPolygons.ids.map(id => {
            const polygon = polygons.get(id);
            return {
                id,
                label: polygon.name,
                color: polygon.color,
                icon: 'polygon'
            };
        });
    }, [overlappingPolygons, polygons]);
    const clearAllPolygons = useCallback(() => {
        if (!dataSourceRef.current)
            return;
        polygons.forEach(polygon => {
            var _a, _b, _c;
            polygon.vertices.forEach(vertex => {
                var _a;
                if (vertex.entity) {
                    (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(vertex.entity);
                }
            });
            if (polygon.outlineEntity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(polygon.outlineEntity);
            }
            if (polygon.polygonEntity) {
                (_b = dataSourceRef.current) === null || _b === void 0 ? void 0 : _b.entities.remove(polygon.polygonEntity);
            }
            if (polygon.labelEntity) {
                (_c = dataSourceRef.current) === null || _c === void 0 ? void 0 : _c.entities.remove(polygon.labelEntity);
            }
        });
        setPolygons(new Map());
        setSelectedPolygon(null);
        activeVertexRef.current = null;
        setActiveVertex(null);
    }, [polygons]);
    const restoreFromConfig = useCallback((configs, startHidden = false) => {
        if (!dataSourceRef.current || !configs || configs.length === 0)
            return;
        configs.forEach(config => {
            var _a, _b, _c;
            const outlineColor = (_a = config.outlineColor) !== null && _a !== void 0 ? _a : config.color;
            const vertices = config.vertices.map((v, idx) => {
                const cartesian = Cesium.Cartesian3.fromDegrees(v.longitude, v.latitude);
                const entity = createVertexEntity(cartesian, outlineColor, idx === 0);
                if (startHidden && entity) {
                    entity.show = false;
                }
                return {
                    id: generateVertexId(),
                    position: cartesian,
                    entity: entity || undefined
                };
            });
            const positions = vertices.map(v => v.position);
            const fillOpacity = (_b = config.fillOpacity) !== null && _b !== void 0 ? _b : DEFAULT_POLYGON_FILL_OPACITY;
            const outlineEntity = createOutlineEntity(positions, outlineColor, true);
            const polygonEntity = createPolygonEntity(positions, config.color, fillOpacity);
            const areaSquareMeters = calculatePolygonArea(positions);
            const perimeterMeters = calculatePerimeter(positions);
            const edges = calculateEdges(positions);
            const unit = config.unit || defaultUnit;
            if (startHidden) {
                if (outlineEntity)
                    outlineEntity.show = false;
                if (polygonEntity)
                    polygonEntity.show = false;
            }
            const polygon = {
                id: config.id,
                name: config.name,
                vertices,
                edges,
                polygonEntity: polygonEntity || undefined,
                outlineEntity: outlineEntity || undefined,
                labelEntity: undefined,
                areaSquareMeters,
                perimeterMeters,
                color: config.color,
                outlineColor: config.outlineColor,
                displayUnit: unit,
                lengthUnit: config.lengthUnit || unitPrefs.distanceUnit,
                projectId: config.projectId,
                fillOpacity,
                showMeasurements: (_c = config.showMeasurements) !== null && _c !== void 0 ? _c : false
            };
            setPolygons(prev => {
                const newMap = new Map(prev);
                newMap.set(config.id, polygon);
                return newMap;
            });
            fetchEdgeProfiles(config.id, edges, positions);
        });
    }, [createVertexEntity, createOutlineEntity, createPolygonEntity, createLabelEntity, defaultUnit, fetchEdgeProfiles]);
    // Rebuild every polygon from a geometry snapshot (used by undo/redo). Clears
    // the live set first so removed polygons disappear and moved ones snap back.
    // The current selection is preserved across the rebuild so the polygon's
    // on-map annotations (side labels + rotate handle) stay put instead of
    // vanishing when you undo an edit on it.
    const restoreGeometrySnapshot = useCallback((configs) => {
        const keep = selectedPolygon;
        // Reset the rotate knob to north for the restored geometry. The knob bearing
        // is a ref that persists between gestures; since re-selecting the same
        // polygon doesn't re-run the handle effect, without this the knob would stay
        // at the pre-undo (rotated) bearing while the shape snaps back.
        handleBearingRef.current = 0;
        rotationDegRef.current = 0;
        isRotatingRef.current = false;
        clearAllPolygons();
        restoreFromConfig(configs);
        if (keep && configs.some(c => c.id === keep.polygonId)) {
            selectPolygonById(keep.polygonId, keep.screenPosition);
        }
    }, [clearAllPolygons, restoreFromConfig, selectedPolygon, selectPolygonById]);
    const undoEdit = useCallback(() => {
        if (undoStackRef.current.length === 0)
            return;
        redoStackRef.current.push(captureConfigs());
        const snap = undoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    const redoEdit = useCallback(() => {
        if (redoStackRef.current.length === 0)
            return;
        undoStackRef.current.push(captureConfigs());
        const snap = redoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z redo — scoped to when the polygon tool is
    // armed or a polygon is selected, so it doesn't hijack the shortcut elsewhere.
    // Suppressed while typing.
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z')
                return;
            if (!isActive && !selectedPolygon)
                return;
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if (inEditable)
                return;
            e.preventDefault();
            if (e.shiftKey)
                redoEdit();
            else
                undoEdit();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive, selectedPolygon, undoEdit, redoEdit]);
    // Cancel drawing when tool becomes inactive
    useEffect(() => {
        if (!isActive && drawing.points.length > 0) {
            drawing.clearAll();
        }
    }, [isActive, drawing]);
    // Delete / Backspace removes the active (yellow) vertex on a FINISHED polygon
    // — parity with the in-draw shortcut and with right-click delete, but for the
    // common "I accidentally added a point" case. Suppressed while typing. If the
    // polygon would drop below 3 vertices, deleteVertex removes it whole (same as
    // right-click). Drawing-phase deletes are handled by the effect below, which
    // only runs while points are being placed.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace')
                return;
            if (drawing.points.length > 0)
                return; // mid-draw handled elsewhere
            const active = activeVertexRef.current;
            if (!active)
                return;
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if (inEditable)
                return;
            const poly = polygonsRef.current.get(active.polygonId);
            if (!poly)
                return;
            const idx = poly.vertices.findIndex(v => v.id === active.vertexId);
            if (idx < 0)
                return;
            e.preventDefault();
            deleteVertex(active.polygonId, idx);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [deleteVertex, drawing.points.length]);
    // Keyboard shortcuts during polygon drawing — matches the path tool 1:1
    // so users build muscle memory across both:
    //   Esc            cancel and discard the in-progress polygon
    //   Backspace/Del  delete the selected vertex, or (if none) the last placed
    //   Enter          finish the polygon (>= 3 vertices)
    // All suppressed when focus is in a text input so renaming/searching
    // mid-draw doesn't blow away the shape.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (drawing.points.length === 0)
                return;
            if (e.key === 'Escape') {
                e.preventDefault();
                drawing.clearAll();
                return;
            }
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable);
            if (inEditable)
                return;
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                if (drawing.selectedVertexIndex >= 0) {
                    drawing.deleteSelectedVertex();
                }
                else {
                    drawing.removeLastPoint();
                }
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (drawing.points.length >= 3) {
                    finishPolygonRef.current();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [drawing]);
    useEffect(() => {
        if (selectedPolygon && !isItemVisible(selectedPolygon.polygonId)) {
            setSelectedPolygon(null);
        }
    }, [selectedPolygon, isItemVisible]);
    const serializeForStorage = useCallback(() => {
        const existingJson = localStorage.getItem(STORAGE_KEY);
        return serializePolygonsForStorage(polygons, projectId, existingJson);
    }, [polygons, projectId]);
    const restoreFromStorage = useCallback((stored) => {
        if (!projectId)
            return;
        const configs = deserializeStoredPolygons(stored, projectId);
        restoreFromConfig(configs, true);
    }, [restoreFromConfig, projectId]);
    const { saveToStorage } = useLocalStoragePersistence({
        storageKey: STORAGE_KEY,
        serialize: serializeForStorage,
        restore: restoreFromStorage,
        isReady: !!dataSourceRef.current && !!projectId,
        resetKey: projectId
    });
    useEffect(() => {
        saveToStorage();
    }, [polygons, saveToStorage]);
    const handleRightClickDelete = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        // Try scene.pick() first
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [polygonId, polygon] of polygons.entries()) {
                for (let i = 0; i < polygon.vertices.length; i++) {
                    if (entity === polygon.vertices[i].entity) {
                        deleteVertex(polygonId, i);
                        return true;
                    }
                }
            }
        }
        // Fallback: proximity-based search in screen space
        const SCREEN_THRESHOLD = 25;
        let closestDist = SCREEN_THRESHOLD;
        let closestPolygonId = null;
        let closestVertexIndex = -1;
        for (const [polygonId, polygon] of polygons.entries()) {
            for (let i = 0; i < polygon.vertices.length; i++) {
                const vertexScreenPos = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, polygon.vertices[i].position);
                if (!vertexScreenPos)
                    continue;
                const dx = event.position.x - vertexScreenPos.x;
                const dy = event.position.y - vertexScreenPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < closestDist) {
                    closestDist = dist;
                    closestPolygonId = polygonId;
                    closestVertexIndex = i;
                }
            }
        }
        if (closestPolygonId !== null && closestVertexIndex >= 0) {
            deleteVertex(closestPolygonId, closestVertexIndex);
            return true;
        }
        return false;
    }, [viewer, polygons, deleteVertex]);
    const isEntityDraggable = useCallback((entity) => {
        // The rotate knob is grabbable.
        if (entity === rotateHandleEntityRef.current)
            return true;
        // Check completed polygon vertices, plus the body/label (whole-polygon
        // move) so hovering them shows the grab cursor — the move affordance is
        // otherwise invisible.
        for (const [, polygon] of polygons.entries()) {
            for (const vertex of polygon.vertices) {
                if (entity === vertex.entity)
                    return true;
            }
            if (entity === polygon.polygonEntity || entity === polygon.labelEntity)
                return true;
        }
        // Check drawing point entities
        if (drawing.cartesianPoints.length > 0) {
            // The drawing hook manages its own point entities internally via pointEntitiesRef,
            // but we can't access those directly. The drawing hook's isDragging state covers this.
        }
        return false;
    }, [polygons, drawing.cartesianPoints.length]);
    // True when the entity is a polygon body/label — the whole-polygon MOVE
    // target (as opposed to a vertex/knob). Drives the dedicated move cursor.
    const isBodyEntity = useCallback((entity) => {
        for (const [, polygon] of polygons.entries()) {
            if (entity === polygon.polygonEntity || entity === polygon.labelEntity)
                return true;
        }
        return false;
    }, [polygons]);
    const createPolygonFromCoordinates = useCallback((coordinates, name, color, lineWidth, fillOpacity, outlineColor) => {
        if (!dataSourceRef.current || coordinates.length < 3)
            return null;
        const polygonId = generatePolygonId();
        const polygonName = name || nextDefaultPolygonName(polygons);
        const polygonColor = color || DEFAULT_POLYGON_COLOR;
        const resolvedOutlineColor = outlineColor !== null && outlineColor !== void 0 ? outlineColor : polygonColor;
        const positions = coordinates.map(coord => Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat));
        const vertices = positions.map((pos, idx) => ({
            id: generateVertexId(),
            position: pos,
            entity: createVertexEntity(pos, resolvedOutlineColor, idx === 0) || undefined
        }));
        const resolvedFillOpacity = typeof fillOpacity === 'number' ? fillOpacity : DEFAULT_POLYGON_FILL_OPACITY;
        const outlineEntity = createOutlineEntity(positions, resolvedOutlineColor, true, lineWidth);
        const polygonEntity = createPolygonEntity(positions, polygonColor, resolvedFillOpacity);
        const areaSquareMeters = calculatePolygonArea(positions);
        const perimeterMeters = calculatePerimeter(positions);
        const edges = calculateEdges(positions);
        const newPolygon = {
            id: polygonId,
            name: polygonName,
            vertices,
            edges,
            polygonEntity: polygonEntity || undefined,
            outlineEntity: outlineEntity || undefined,
            labelEntity: undefined,
            areaSquareMeters,
            perimeterMeters,
            color: polygonColor,
            outlineColor,
            displayUnit: unitPrefs.areaUnit,
            lengthUnit: unitPrefs.distanceUnit,
            projectId: projectId || undefined,
            lineWidth,
            fillOpacity: resolvedFillOpacity
        };
        setPolygons(prev => {
            const newMap = new Map(prev);
            newMap.set(polygonId, newPolygon);
            return newMap;
        });
        fetchEdgeProfiles(polygonId, edges, positions);
        fetchSurfaceArea(polygonId, vertices, areaSquareMeters);
        return polygonId;
    }, [polygons, unitPrefs.areaUnit, fetchEdgeProfiles, fetchSurfaceArea, createVertexEntity, createOutlineEntity, createPolygonEntity, createLabelEntity, projectId]);
    // Duplicate a finished polygon: clone its vertices at a small SE offset
    // (shared with the measure tool's duplicate) so the copy is visibly
    // distinct, then auto-select it so its dialog opens for immediate
    // dragging/editing. Carries the source's color/outline/width/opacity.
    const duplicatePolygon = useCallback((polygonId) => {
        const polygon = polygonsRef.current.get(polygonId);
        if (!polygon || polygon.vertices.length < 3)
            return null;
        const coords = polygon.vertices.map(v => {
            const c = Cesium.Cartographic.fromCartesian(v.position);
            return { lat: Cesium.Math.toDegrees(c.latitude), lng: Cesium.Math.toDegrees(c.longitude) };
        });
        const { dLat, dLng } = computeCopyOffsetLatLng(coords[0].lat);
        const offsetCoords = coords.map(c => ({ lat: c.lat + dLat, lng: c.lng + dLng }));
        pushUndoSnapshot();
        const newId = createPolygonFromCoordinates(offsetCoords, `${polygon.name} copy`, polygon.color, polygon.lineWidth, polygon.fillOpacity, polygon.outlineColor);
        if (newId)
            selectPolygonById(newId);
        return newId;
    }, [createPolygonFromCoordinates, selectPolygonById]);
    // Rectangle mode: the drawing hook hands us four corners on the second
    // click. Build the finished polygon via the same create-from-coordinates
    // path as upload/duplicate, then auto-select it so its dialog opens.
    useEffect(() => {
        rectangleCompleteRef.current = (corners) => {
            pushUndoSnapshot();
            const newId = createPolygonFromCoordinates(corners);
            if (newId)
                selectPolygonById(newId);
        };
    }, [createPolygonFromCoordinates, selectPolygonById]);
    // Switching draw mode discards any in-progress sketch so a half-drawn
    // freeform polygon can't bleed into rectangle mode (and vice versa).
    const prevDrawModeRef = useRef(polygonDrawMode);
    useEffect(() => {
        if (prevDrawModeRef.current !== polygonDrawMode) {
            drawing.clearAll();
            prevDrawModeRef.current = polygonDrawMode;
        }
    }, [polygonDrawMode, drawing]);
    // Cmd/Ctrl+D duplicates the currently-selected polygon (dialog open). Gated
    // on an open selection so it doesn't hijack the browser's bookmark shortcut
    // during normal use, and suppressed while typing in a field.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'd')
                return;
            if (!selectedPolygon)
                return;
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable);
            if (inEditable)
                return;
            e.preventDefault();
            duplicatePolygon(selectedPolygon.polygonId);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedPolygon, duplicatePolygon]);
    return {
        polygons,
        selectedPolygon,
        dragging,
        deleteVertexPrompt,
        defaultUnit,
        isVisible,
        // Drawing state from usePolygonDrawing
        isDrawing: drawing.points.length > 0,
        drawingPointCount: drawing.points.length,
        drawingAreaM2: drawing.areaM2,
        drawingIsDragging: drawing.isDragging,
        selectedDrawingVertexIndex: drawing.selectedVertexIndex,
        deleteSelectedDrawingVertex: drawing.deleteSelectedVertex,
        // Freeform vs rectangle draw mode (mirrors measureMode)
        polygonDrawMode,
        setPolygonDrawMode,
        rectanglePending: drawing.rectanglePending,
        // Draw-time side-length labels opt-out (persisted)
        showDrawLabels,
        setShowDrawLabels,
        // Handlers - note: most click/drag handled by usePolygonDrawing internally
        handleMapClick,
        handleMapHover,
        handleMapMouseDown,
        handleMapMouseUp,
        checkExistingPolygonClick,
        handleActiveAnchorInsert,
        handleRightClickDelete,
        isEntityDraggable,
        isBodyEntity,
        finishPolygon,
        cancelPending,
        undoLastPoint: drawing.removeLastPoint,
        removePolygon,
        deleteVertex,
        confirmDeleteVertex,
        cancelDeleteVertex,
        updatePolygonColor,
        updatePolygonOutlineColor,
        updatePolygonLineWidth,
        updatePolygonFillOpacity,
        updatePolygonName,
        updatePolygonUnit,
        setPolygonLengthUnit,
        setPolygonShowMeasurements,
        updatePolygonOpacity,
        getItemOpacity,
        dismissSelection,
        selectPolygonById,
        clearAllPolygons,
        restoreFromConfig,
        hideAll,
        showAll,
        toggleItemVisibility,
        isItemVisible,
        highlightEdge,
        hoveredEdge,
        polygonCount: polygons.size,
        createPolygonFromCoordinates,
        duplicatePolygon,
        // Overlap detection for SpiderPicker
        overlappingPolygons,
        handleOverlapSelect,
        dismissOverlapPicker,
        getOverlapSpiderItems
    };
};
